<template>
  <div class="flex flex-col gap-10">
    <!-- Sección de títulos -->
    <div class="flex flex-col justify-between lg:flex-row gap-4">
      <titlePrimary :title="isEdit ? $t('gasForm.titles.edit') : $t('gasForm.titles.create')" />
      <titlePrimary
        class="px-20 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
        bgColor="bg-[#00000]"
        textColor="text-[#7CADB9]"
        :title="$t('gasForm.subtitle')"
      />
    </div>

    <div class="grid lg:grid-cols-3 md:grid-cols-2 grid-col-1 gap-10">
      <!-- Columna 1 -->
      <div>
        <div class="relative">
          <inputPrimary
            v-model="searchText"
            :label="$t('gasForm.fields.searchProperty')"
            :placeholder="$t('gasForm.fields.searchPlaceholder')"
          />

          <div v-if="searchLoading" class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg max-h-60 overflow-auto">
            <p class="text-gray-500">{{ $t('common.loading') }}</p>
          </div>

          <div
            v-else-if="searchResults && searchResults.data && searchResults.data.length > 0"
            class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg max-h-60 overflow-auto"
          >
            <ul>
              <li
                v-for="item in searchResults.data"
                :key="item.id"
                class="hover:bg-gray-100 cursor-pointer p-2"
                @click="selectSearchResult(item)"
              >
                <p class="truncate">{{ item.name }} · {{ item.address }}</p>
              </li>
            </ul>
          </div>

          <div v-else-if="searchText && searchResults && !searchResults.data.length" class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg">
            <p>{{ $t('gasForm.fields.noResults') }}</p>
          </div>
        </div>

        <inputPrimary
          v-model="formData.cups"
          :label="$t('gasForm.fields.cups')"
          :placeholder="$t('gasForm.fields.cupsPlaceholder')"
        />
        <inputPrimary
          v-model="formData.address"
          :label="$t('gasForm.fields.address')"
          :placeholder="$t('gasForm.fields.addressPlaceholder')"
        />

        <div class="mt-2">
          <label class="block text-base mb-2 text-[#385660] truncate cursor-help" title="Liquidar Gas">Liquidar Gas:</label>
          <div class="flex items-center border border-[#3A5762] rounded-lg px-3 h-11 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
            <input
              type="checkbox"
              id="liquidate_gas"
              v-model="formData.liquidate_gas"
              class="w-4 h-4 text-[#25B0D7] bg-gray-100 border-gray-300 rounded focus:ring-[#25B0D7] focus:ring-2 mr-3 flex-shrink-0"
            />
            <label for="liquidate_gas" class="text-sm px-1 text-[#7CADB9] cursor-pointer select-none">
              Marcar para liquidar
            </label>
          </div>
        </div>
      </div>

      <!-- Columna 2 -->
      <div>
        <!-- Campo compañía con dropdown custom -->
        <div style="margin: 8px 0;">
          <label class="block text-base mb-2 text-[#385660] truncate cursor-help">
            {{ $t('gasForm.fields.company') }}
          </label>
          <div class="relative" ref="companyDropdownRef">
            <div
              class="flex items-center border border-[#3A5762] rounded-lg px-3 h-11 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] cursor-pointer"
              :class="companyLoading ? 'opacity-60 cursor-not-allowed' : ''"
              @click="toggleCompanyDropdown"
            >
              <div class="w-full text-left text-sm truncate" style="color: var(--c-primary3)">
                {{ selectedCompany || $t('gasForm.fields.companyPlaceholder') }}
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" style="color: var(--c-primary2)">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
              </svg>
            </div>

            <div v-if="companyDropdownOpen" class="absolute left-0 right-0 z-50 mt-1 border rounded p-2 bg-white shadow-lg max-h-60 overflow-auto">
              <ul>
                <li v-if="companyLoading" class="p-2 text-gray-500">{{ $t('common.loading') }}</li>
                <li v-else-if="companies.length === 0" class="p-2 text-gray-500">{{ $t('gasForm.fields.noResults') }}</li>
                <li
                  v-for="company in companies"
                  :key="company.id"
                  class="p-2 hover:bg-gray-100 cursor-pointer truncate"
                  @click.stop="selectCompany(company)"
                  style="color: var(--c-primary3)"
                >
                  {{ company.name }}
                </li>
              </ul>
            </div>
          </div>
          <span v-if="companyLoading" class="text-xs text-gray-400">{{ $t('common.loading') }}</span>
          <span v-if="validationErrors.company" class="text-red-500 text-xs">{{ validationErrors.company }}</span>
        </div>

        <inputPrimary
          v-model="formData.rate"
          :label="$t('gasForm.fields.rate')"
          :placeholder="$t('gasForm.fields.ratePlaceholder')"
        />
        <inputPrimary
          v-model="formData.contractEnd"
          :label="$t('gasForm.fields.contractEnd')"
          :placeholder="$t('gasForm.fields.contractEndPlaceholder')"
          type="date"
        />
        <inputPrimary
          v-model="formData.cif"
          :label="$t('supplyGas.details.labels.cif')"
        />
        <inputPrimary
          v-model="formData.iban"
          :label="$t('supplyGas.details.labels.iban')"
        />
      </div>

      <!-- Columna 3 -->
      <div class="flex flex-col gap-2 items-end justify-between">
        <textareaPrimary
          v-model="formData.observations"
          :label="$t('gasForm.fields.comments')"
          :placeholder="$t('gasForm.fields.commentsPlaceholder')"
          textareaClass="h-[165px]"
        />
      </div>
    </div>

    <div class="flex justify-end">
      <div class=" lg:w-1/3 w-full p-4 flex items-center justify-center">
        <ButtonWhite
          @click="submitForm"
          :disabled="submitting"
          :class="[submitting ? 'opacity-50 cursor-not-allowed w-full' : 'w-full']"
          bgColor="bg-[#25B0D7]"
          textColor="text-white"
          hoverBgColor="bg-[#2AB4D9]"
        >
          {{ isEdit ? $t('supplyLight.buttons.saveChanges') : $t('supplyLight.buttons.save') }}
        </ButtonWhite>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isValidContractCups, normalizeContractCups } from '@/services/contractCups';
import titlePrimary from '@/components/title/title-primary.vue';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import inputPrimary from '@/components/inputs/input-primary.vue';
import textareaPrimary from '@/components/inputs/textarea-primary.vue';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const selectedOption = ref(null);
const route = useRoute();
const router = useRouter();
const id = route.params.id;

const isEdit = ref(!!id);

const loading = ref(false);
const submitting = ref(false);
const searchLoading = ref(false);
const companyLoading = ref(false);

const formData = ref({
  type: 'gas',
  status: 'created',
  cups: route.query.cups || '',
  address: '',
  company: route.query.company || '',
  cif: route.query.cif || '',
  iban: route.query.iban || '',
  rate: route.query.rate || '',
  contractEnd: route.query.contractEnd || '',
  observations: route.query.observations || '',
  propertyName: route.query.propertyName || '',
  propertyId: '',
  ownerId: route.query.ownerId?.toString() || '',
  liquidate_gas: false
});

const searchText = ref(route.query.propertyName || '');
const searchResults = ref(null);
const companies = ref([]);
const validationErrors = ref({});

// Custom company dropdown state
const companyDropdownOpen = ref(false);
const companyDropdownRef = ref(null);
const selectedCompany = ref(formData.value.company || '');

const toggleCompanyDropdown = () => {
  if (companyLoading.value) return;
  companyDropdownOpen.value = !companyDropdownOpen.value;
};

const selectCompany = (company) => {
  formData.value.company = company.name;
  selectedCompany.value = company.name;
  companyDropdownOpen.value = false;
};

const closeCompanyDropdown = () => {
  companyDropdownOpen.value = false;
};

const handleDocumentClick = (e) => {
  if (companyDropdownRef.value && !companyDropdownRef.value.contains(e.target)) {
    closeCompanyDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
});

watch(() => formData.value.company, (val) => {
  selectedCompany.value = val || '';
});

// Bandera para evitar que el watch relance la búsqueda al seleccionar un resultado
const skipNextSearch = ref(false);

watch(() => formData.value.type, (newValue) => {
  selectedOption.value = newValue;
});

const performSearch = async (query) => {
  if (!query || query.length < 2) {
    searchResults.value = null;
    return;
  }

  searchLoading.value = true;
  try {
    const endpoint = '/supply/properties/search';

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${endpoint}`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          query: query,
          per_page: 100,
        }
      }
    );

    if (response.data.value?.data) {
      searchResults.value = response.data.value;
    } else {
      searchResults.value = null;
    }
  } catch (error) {
    console.error('Error en la búsqueda:', error);
    searchResults.value = null;
  } finally {
    searchLoading.value = false;
  }
};

let searchTimeout;
watch(searchText, (newSearchText) => {
  if (!isEdit.value && newSearchText !== formData.value.propertyName) formData.value.propertyId = '';
  // Si acabamos de seleccionar un resultado, ignoramos este disparo del watch
  if (skipNextSearch.value) {
    skipNextSearch.value = false;
    return;
  }

  if (searchTimeout) clearTimeout(searchTimeout);

  if (newSearchText && newSearchText.length >= 2) {
    searchTimeout = setTimeout(() => {
      performSearch(newSearchText);
    }, 300);
  } else {
    searchResults.value = null;
  }
});

const selectSearchResult = (result) => {
  skipNextSearch.value = true; // Evita que el watch vuelva a buscar
  formData.value.propertyName = result.name || result.address;
  formData.value.propertyId = result.id;
  formData.value.ownerId = result.propietario?.name || '';
  searchText.value = result.name || result.address;
  searchResults.value = null;
};

const submitForm = async () => {
  if (submitting.value) return;
  if (!isValidContractCups(formData.value.cups)) {
    toast.error(t('liquidation.invalidCups'));
    return;
  }
  if (!formData.value.cups) {
    toast.error(t('gasForm.validation.cupsRequired'));
    return;
  }
  if (!formData.value.address) {
    toast.error(t('gasForm.validation.addressRequired'));
    return;
  }
  if (!formData.value.company) {
    toast.error(t('gasForm.validation.companyRequired'));
    return;
  }
  if (!formData.value.rate) {
    toast.error(t('gasForm.validation.rateRequired'));
    return;
  }
  if (!formData.value.contractEnd) {
    toast.error(t('gasForm.validation.contractEndRequired'));
    return;
  }
  if (!formData.value.propertyName || !formData.value.propertyId) {
    toast.error(t('liquidation.selectProperty'));
    return;
  }

  submitting.value = true;
  try {
    let response;
    if (isEdit.value) {
        response = await axios.put(
        `${import.meta.env.VITE_API_URL}/supply/update/${id}`,
        {
          type: 'gas',
          status: 'created',
          cups: normalizeContractCups(formData.value.cups),
          address: formData.value.address,
          company: formData.value.company,
          cif: formData.value.cif || null,
          iban: formData.value.iban || null,
          rate: formData.value.rate,
            contract_expiration: formData.value.contractEnd,
          observations: formData.value.observations,
          propertyName: formData.value.propertyName,
            liquidate: formData.value.liquidate_gas ? 'liquidate' : ''
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Suministro actualizado correctamente');
    } else {
      const targetPropertyId = formData.value.propertyId;
      response = await axios.post(
        `${import.meta.env.VITE_API_URL}/supply/create/${targetPropertyId}`,
        {
          type: 'gas',
          status: 'created',
          cups: normalizeContractCups(formData.value.cups),
          address: formData.value.address,
          company: formData.value.company,
          cif: formData.value.cif || null,
          iban: formData.value.iban || null,
          rate: formData.value.rate,
          contract_expiration: formData.value.contractEnd,
          observations: formData.value.observations,
          propertyName: formData.value.propertyName,
          liquidate: formData.value.liquidate_gas ? 'liquidate' : ''
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Suministro creado correctamente');
    }

    if (response.status === 200 || response.status === 201) {
      setTimeout(() => {
        router.push(`/gas-detail/${response.data.value.id}`);
      }, 1500);
    } else {
      toast.error('Error al guardar el suministro');
    }
  } catch (error) {
    console.error('Error al guardar el suministro:', error);
    if (error.response?.status === 422) {
      const errors = error.response.data?.errors;
      if (errors) {
        const messages = Object.entries(errors)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('\n');
        console.error('Errores de validación:', messages);
        toast.error('Errores: ' + Object.values(errors).flat().join(' | '));
      } else {
        toast.error(error.response.data?.message || 'Error de validación');
      }
    } else {
      toast.error('Error al guardar el suministro');
    }
  } finally {
    submitting.value = false;
  }
};

const fetchFincaData = async () => {
  if (!id) return;
  loading.value = true;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/supply/get/${id}`,
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    if (response.data?.value) {
      const supplyData = response.data.value;
      const contractDate = new Date(supplyData.contract_expiration);
      const formattedDate = contractDate.toISOString().split('T')[0];

      formData.value = {
        type: supplyData.type,
        status: supplyData.status,
        cups: supplyData.cups,
        address: supplyData.address,
        company: supplyData.company,
        cif: supplyData.cif || '',
        iban: supplyData.iban || '',
        rate: supplyData.rate,
        contractEnd: formattedDate,
        observations: supplyData.observations,
        propertyName: supplyData.property?.name || '',
        propertyId: supplyData.property?.id || '',
        ownerId: supplyData.property?.propietario?.name || '',
        liquidate_gas: supplyData.liquidate_gas === 'liquidate' || false
      };

      skipNextSearch.value = true; // Evita búsqueda al cargar datos en edición
      searchText.value = supplyData.property?.name || '';
    }
  } catch (error) {
    console.error('Error loading property data:', error);
  } finally {
    loading.value = false;
  }
};

const fetchCompanies = async () => {
  companyLoading.value = true;
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/company`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Accept': 'application/json'
      }
    });
    if (Array.isArray(response.data)) {
      companies.value = response.data;
    } else if (response.data?.value && Array.isArray(response.data.value)) {
      companies.value = response.data.value;
    } else {
      companies.value = [];
    }
  } catch {
    companies.value = [];
  } finally {
    companyLoading.value = false;
  }
};

onMounted(() => {
  fetchCompanies();
  if (isEdit.value) fetchFincaData();
});
</script>

<style scoped>
.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}
</style>
